import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bike, CreditCard, MapPin, Package } from 'lucide-react';

import './EnviosViagens.css';
import { backendGet } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import AddressMapPicker from '@/components/AddressMapPicker';
import { reverseGeocode } from '@/services/routes';
import { createMercadoPagoPreference } from '@/services/payments';
import { openExternalUrl } from '@/mobile/navigation';
import {
  createEnviosViagensOrder,
  estimateEnviosViagens,
  getEnviosViagensConfig,
} from '@/services/enviosViagens';

function parseCoord(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDistanceKm(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `${num.toFixed(1).replace('.', ',')} km`;
}

function normalizeAddressList(payload) {
  if (Array.isArray(payload)) return payload.filter((item) => item && typeof item === 'object');
  if (payload && typeof payload === 'object') return [payload];
  return [];
}

function resolvePrimaryAddress(addresses) {
  if (!Array.isArray(addresses) || addresses.length === 0) return null;
  return addresses.find((item) => item?.is_primary) || addresses[0];
}

function addressToLocation(address) {
  if (!address || typeof address !== 'object') return null;
  const lat = parseCoord(address.lat ?? address.latitude);
  const lng = parseCoord(address.lng ?? address.longitude);
  if (lat == null || lng == null) return null;
  return {
    label: address.label || null,
    street: address.street || null,
    number: address.number || null,
    complement: address.complement || null,
    neighborhood: address.neighborhood || null,
    city: address.city || null,
    state: address.state || null,
    zipcode: address.zipcode || null,
    lat,
    lng,
  };
}

function locationLabel(location) {
  if (!location || typeof location !== 'object') return 'Nao definido';
  const parts = [
    location.label,
    location.street,
    location.number,
    location.neighborhood,
    location.city,
    location.state,
  ].filter(Boolean);
  if (parts.length) return parts.join(', ');
  if (Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
    return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;
  }
  return 'Nao definido';
}

const PAYMENT_OPTIONS = [
  { value: 'pix', label: 'PIX' },
  { value: 'credit_card', label: 'Cartao de credito' },
  { value: 'debit_card', label: 'Cartao de debito' },
];

export default function EnviosViagens() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [serviceKind, setServiceKind] = useState('viagem');
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [step, setStep] = useState('request');
  const [note, setNote] = useState('');
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [primaryAddress, setPrimaryAddress] = useState(null);
  const [estimate, setEstimate] = useState(null);

  const serviceLabel = serviceKind === 'envio' ? 'Envio' : 'Viagem';

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const [configResponse, profileResponse] = await Promise.all([
          getEnviosViagensConfig(),
          backendGet('/api/auth/profile'),
        ]);
        if (!mounted) return;

        setConfig(configResponse || null);
        const addresses = normalizeAddressList(profileResponse?.addresses || profileResponse?.address);
        const primary = resolvePrimaryAddress(addresses);
        const primaryLocation = addressToLocation(primary);
        setPrimaryAddress(primaryLocation);
        setPickup(primaryLocation);
      } catch (error) {
        if (!mounted) return;
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Nao foi possivel carregar Envios e Viagens.',
        });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [toast]);

  async function hydrateAddressFromCoords(lat, lng, applyFn) {
    try {
      const response = await reverseGeocode({ lat, lng });
      applyFn((prev) => ({
        ...(prev || {}),
        lat,
        lng,
        street: response?.street || prev?.street || null,
        number: response?.number || prev?.number || null,
        neighborhood: response?.neighborhood || prev?.neighborhood || null,
        city: response?.city || prev?.city || null,
        state: response?.state_code || response?.state || prev?.state || null,
        zipcode: response?.zipcode || prev?.zipcode || null,
        label: response?.label || prev?.label || null,
      }));
    } catch {
      applyFn((prev) => ({ ...(prev || {}), lat, lng }));
    }
  }

  async function handleCalculate() {
    const hasPickup = Number.isFinite(parseCoord(pickup?.lat)) && Number.isFinite(parseCoord(pickup?.lng));
    const hasDestination = Number.isFinite(parseCoord(destination?.lat)) && Number.isFinite(parseCoord(destination?.lng));

    if (!hasPickup) {
      toast({
        variant: 'destructive',
        title: 'Origem obrigatoria',
        description: 'Defina a origem no mapa para continuar.',
      });
      return;
    }
    if (!hasDestination) {
      toast({
        variant: 'destructive',
        title: 'Destino obrigatorio',
        description: 'Selecione o destino no mapa para calcular o valor.',
      });
      return;
    }

    setCalculating(true);
    try {
      const response = await estimateEnviosViagens({
        service_kind: serviceKind,
        pickup,
        destination,
      });
      setEstimate(response || null);
      setStep('request');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro no calculo',
        description: error?.message || 'Nao foi possivel calcular o valor da corrida.',
      });
    } finally {
      setCalculating(false);
    }
  }

  async function handleSubmitOrder() {
    if (!estimate?.pricing) {
      toast({
        variant: 'destructive',
        title: 'Calcule antes',
        description: 'Calcule o valor da corrida antes de prosseguir para pagamento.',
      });
      return;
    }

    setSubmitting(true);
    try {
      const created = await createEnviosViagensOrder({
        service_kind: serviceKind,
        pickup,
        destination,
        payment_method: paymentMethod,
        notes: note || null,
      });
      const orderId = created?.order_id || created?.id;
      if (!orderId) {
        throw new Error('Pedido criado sem identificador valido.');
      }

      const preference = await createMercadoPagoPreference(orderId);
      const initPoint = preference?.init_point || preference?.sandbox_init_point;
      if (!initPoint) {
        throw new Error('Nao foi possivel iniciar o pagamento no momento.');
      }

      await openExternalUrl(initPoint);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao solicitar motoboy',
        description: error?.message || 'Nao foi possivel concluir a solicitacao.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const canProceedToPayment = Boolean(estimate?.pricing);
  const pickupLabel = useMemo(() => locationLabel(estimate?.pickup || pickup), [estimate?.pickup, pickup]);
  const destinationLabel = useMemo(() => locationLabel(estimate?.destination || destination), [estimate?.destination, destination]);

  if (loading) {
    return (
      <div className="envios-page">
        <div className="envios-shell">
          <p>Carregando Envios e Viagens...</p>
        </div>
      </div>
    );
  }

  if (config?.enabled === false) {
    return (
      <div className="envios-page">
        <div className="envios-shell">
          <button className="envios-back-btn" onClick={() => navigate('/perfil?tab=profile')}>
            <ArrowLeft size={16} />
            Voltar para perfil
          </button>
          <div className="envios-disabled-card">
            <h1>Envios e Viagens indisponivel</h1>
            <p>O admin desativou temporariamente este servico.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="envios-page">
      <div className="envios-shell">
        <button className="envios-back-btn" onClick={() => navigate('/perfil?tab=profile')}>
          <ArrowLeft size={16} />
          Voltar para perfil
        </button>

        <header className="envios-header">
          <span className="envios-kicker">Novo servico</span>
          <h1>Envios e Viagens</h1>
          <p>Escolha o tipo, selecione destino no mapa e finalize o pagamento no Correfood.</p>
        </header>

        {step === 'request' ? (
          <section className="envios-section">
            <h2>1. Tipo da solicitacao</h2>
            <div className="envios-kind-grid">
              <button
                type="button"
                className={`envios-kind-card ${serviceKind === 'viagem' ? 'active' : ''}`}
                onClick={() => {
                  setServiceKind('viagem');
                  setEstimate(null);
                }}
              >
                <Bike size={20} />
                <div>
                  <strong>Viagem</strong>
                  <p>Corrida para deslocamento de cliente.</p>
                </div>
              </button>
              <button
                type="button"
                className={`envios-kind-card ${serviceKind === 'envio' ? 'active' : ''}`}
                onClick={() => {
                  setServiceKind('envio');
                  setEstimate(null);
                }}
              >
                <Package size={20} />
                <div>
                  <strong>Envio</strong>
                  <p>Entrega de pacote com motoboy.</p>
                </div>
              </button>
            </div>

            <h2>2. Origem e destino</h2>
            <div className="envios-locations-grid">
              <article className="envios-location-card">
                <div className="envios-location-title">
                  <MapPin size={16} />
                  Origem
                </div>
                <p>{locationLabel(pickup)}</p>
                {primaryAddress ? (
                  <button
                    type="button"
                    className="envios-link-btn"
                    onClick={() => {
                      setPickup(primaryAddress);
                      setEstimate(null);
                    }}
                  >
                    Usar endereco principal do perfil
                  </button>
                ) : null}
                <AddressMapPicker
                  value={pickup && Number.isFinite(pickup.lat) && Number.isFinite(pickup.lng) ? { lat: pickup.lat, lng: pickup.lng } : null}
                  onChange={({ lat, lng }) => {
                    setEstimate(null);
                    hydrateAddressFromCoords(lat, lng, setPickup);
                  }}
                  height={230}
                />
              </article>

              <article className="envios-location-card">
                <div className="envios-location-title">
                  <MapPin size={16} />
                  Destino
                </div>
                <p>{locationLabel(destination)}</p>
                <AddressMapPicker
                  value={destination && Number.isFinite(destination.lat) && Number.isFinite(destination.lng) ? { lat: destination.lat, lng: destination.lng } : null}
                  onChange={({ lat, lng }) => {
                    setEstimate(null);
                    hydrateAddressFromCoords(lat, lng, setDestination);
                  }}
                  height={230}
                />
              </article>
            </div>

            <div className="envios-estimate-card">
              <h3>Resumo da corrida</h3>
              <p>Tipo: <strong>{serviceLabel}</strong></p>
              <p>Origem: <strong>{pickupLabel}</strong></p>
              <p>Destino: <strong>{destinationLabel}</strong></p>
              <p>Regra: {formatMoney(config?.base_fee_reais)} ate {Number(config?.base_radius_km || 0).toFixed(1).replace('.', ',')} km + {formatMoney(config?.extra_km_fee_reais)} por km excedente</p>
              {estimate?.pricing ? (
                <div className="envios-estimate-result">
                  <p>Distancia calculada: <strong>{formatDistanceKm(estimate?.pricing?.distance_km)}</strong></p>
                  <p>Valor da corrida: <strong>{formatMoney(estimate?.pricing?.total_fee_reais)}</strong></p>
                </div>
              ) : (
                <p>Calcule o valor para continuar.</p>
              )}
            </div>

            <div className="envios-actions">
              <button type="button" className="envios-secondary-btn" onClick={handleCalculate} disabled={calculating}>
                {calculating ? 'Calculando...' : 'Calcular valor'}
              </button>
              <button
                type="button"
                className="envios-primary-btn"
                disabled={!canProceedToPayment}
                onClick={() => setStep('payment')}
              >
                Prosseguir para pagamento
              </button>
            </div>
          </section>
        ) : (
          <section className="envios-section">
            <h2>3. Pagamento</h2>
            <div className="envios-payment-card">
              <p><strong>Servico:</strong> {serviceLabel}</p>
              <p><strong>Origem:</strong> {pickupLabel}</p>
              <p><strong>Destino:</strong> {destinationLabel}</p>
              <p><strong>Distancia:</strong> {formatDistanceKm(estimate?.pricing?.distance_km)}</p>
              <p className="envios-payment-total">
                Total: {formatMoney(estimate?.pricing?.total_fee_reais)}
              </p>
            </div>

            <div className="envios-payment-options">
              {PAYMENT_OPTIONS.map((option) => (
                <label key={option.value} className={`envios-payment-option ${paymentMethod === option.value ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="envios-payment-method"
                    value={option.value}
                    checked={paymentMethod === option.value}
                    onChange={() => setPaymentMethod(option.value)}
                  />
                  <CreditCard size={16} />
                  {option.label}
                </label>
              ))}
            </div>

            <label className="envios-note-field">
              Observacoes para o motoboy (opcional)
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Ex.: pacote fragil, aguardar na portaria, levar capacete extra."
                maxLength={800}
              />
            </label>

            <div className="envios-actions">
              <button type="button" className="envios-secondary-btn" onClick={() => setStep('request')} disabled={submitting}>
                Voltar
              </button>
              <button type="button" className="envios-primary-btn" onClick={handleSubmitOrder} disabled={submitting}>
                {submitting ? 'Iniciando pagamento...' : 'Solicitar motoboy e pagar'}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
